import { useEffect, useState } from "react";
import { listMonthlyReports, generateMonthlyReport, monthlyReportPdfUrl, resendMonthlyReport, apiError } from "@/lib/api";
import { Card, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, FileDown, Send, Calendar } from "lucide-react";

function prevMonth() {
  const d = new Date();
  d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function label(period) {
  try { return new Date(period + "-01").toLocaleString(undefined, { month: "long", year: "numeric" }); } catch { return period; }
}

export function MonthlyReports() {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(prevMonth());
  const [busy, setBusy] = useState(false);

  const load = () => listMonthlyReports().then(setReports).catch((e) => { setReports([]); toast.error(apiError(e)); });
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await generateMonthlyReport(period);
      toast.success(`Report for ${label(period)} generated & ${r.email_status === "sent" ? "emailed to the owner" : r.email_status}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resend = async (id) => {
    try { const r = await resendMonthlyReport(id); toast.success(`Report ${r.status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!reports) return <Loading />;

  return (
    <div data-testid="monthly-reports">
      <div className="flex justify-between items-center mb-4 gap-3">
        <p className="text-sm text-slate-500 hidden sm:block">Company-wide + per-team breakdown of field operations, emailed to the owner. Auto-generated at month-end.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="generate-report-btn"><Calendar className="w-4 h-4 mr-2" /> Generate Report</Button>
      </div>

      {reports.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="reports-empty">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No monthly reports yet. Generate one manually or wait for the automatic month-end run.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="report-list">
          {reports.map((r) => (
            <Card key={r.id} className="p-4 flex items-center justify-between" data-testid={`report-${r.id}`}>
              <div>
                <div className="font-bold text-[#0B132B]">{label(r.period)}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Generated {new Date(r.generated_at).toLocaleDateString()} · email {r.email_status || "—"}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <a href={monthlyReportPdfUrl(r.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid={`report-pdf-${r.id}`}><FileDown className="w-4 h-4 mr-1" /> PDF</Button></a>
                <Button variant="outline" size="sm" onClick={() => resend(r.id)} data-testid={`report-resend-${r.id}`}><Send className="w-4 h-4 mr-1" /> Resend</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="generate-report-dialog">
          <DialogHeader><DialogTitle>Generate monthly team report</DialogTitle><DialogDescription>Select the month. The PDF is emailed to the workspace owner.</DialogDescription></DialogHeader>
          <div className="py-2"><Label>Month</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="report-period-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={busy} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="confirm-generate-btn">{busy ? "Generating…" : "Generate & Email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
